import React, { useState } from "react"

import useDimensions from "react-use-dimensions"
import "./styles.scss"
import DataLoader from "../DataLoader"
import { scaleLinear } from "d3-scale"
import gradient from "../utils/gradient"
import { animated, useTransition } from "react-spring"
import useIsInViewport from "use-is-in-viewport"
import Tooltip from "../Tooltip/"
import useDetectPrint from "use-detect-print"

const printFullWidth = 700

const minGraphHeight = 140
function CountryFactorBarGraph(props) {
  const [tooltipData, setTooltipData] = useState()
  const { selectedCountry, selectedYear, countryData, scoreKey, title } = props
  const [ref, containerDimensions] = useDimensions()
  const [isInViewPort, wrappedRef] = useIsInViewport({ target: ref })
  const isPrinting = useDetectPrint()

  const margins = {
    top: 30,
    left: 60,
    right: 20,
    bottom: 20,
  }
  if (!props.axis) {
    margins.left = 10
    margins.right = margins.left
    margins.bottom = margins.left / 2
  }
  let graphContents = null
  let containerHeight = 0

  const hoverCountry = (country) => {
    return (event) => {
      if (!country) {
        return setTooltipData(null)
      }
      let x = 0
      let y = 0
      if ("clientX" in event) {
        x = event.clientX
        y = event.clientY
      }
      if (event.touches) {
        event.preventDefault()
        event.stopPropagation()
      }
      setTooltipData({ country, x, y })
    }
  }
  let containerWidth = containerDimensions.width || 400

  if (isPrinting) {
    containerWidth = printFullWidth
  }
  let width = containerWidth - margins.left - margins.right
  containerHeight = Math.max(containerWidth / 5, minGraphHeight)
  let height = containerHeight - margins.top - margins.bottom

  const countriesSortedByScore = [
    ...DataLoader.dataByYear[selectedYear].countries,
  ]
  const countriesSortedAlpha = [
    ...DataLoader.dataByYear[selectedYear].countries,
  ]

  countriesSortedAlpha.sort((a, b) => (a.Country > b.Country ? 1 : -1))
  countriesSortedByScore.sort((a, b) => a[scoreKey] - b[scoreKey])

  const yScale = scaleLinear().domain([0, 1]).range([0, height])

  const xScale = scaleLinear()
    .domain([0, countriesSortedAlpha.length - 1])
    .range([0, width])

  const barWidth = xScale(0.9)

  let selectedValues = null

  const getBarDimensions = (country) => {
    const countryScoreIndex = countriesSortedByScore.findIndex(
      (d) => d.Country === country.Country,
    )
    const score = country[scoreKey]
    const x = xScale(countryScoreIndex)
    // Blank 2015/2016 sub-factor scores are null: draw no bar
    const barHeight = score === null ? 0 : yScale(score.toFixed(2))
    const y = height - barHeight
    let fill = gradient(score)
    const countryIsSelected = country.Country === selectedCountry
    if (countryIsSelected) {
      selectedValues = { x, score, y, transform: `translate(${x}, ${y})` }
      fill = "black"
    }
    if (props.rankAttribute !== "Global" && !countryIsSelected) {
      fill =
        country[props.rankAttribute] !== countryData[props.rankAttribute]
          ? "#EBEBEB"
          : fill
    }
    return { x, y, height: barHeight, fill }
  }

  const transitions = useTransition(
    containerDimensions.width ? countriesSortedAlpha : [],
    (d) => d.Country,
    {
      enter: getBarDimensions,
      update: getBarDimensions,
      immediate: !isInViewPort,
    },
  )

  const bars = transitions.map((data, countryIndex) => {
    if (data.state === "leave") {
      return null
    }
    const country = data.item
    const springProps = data.props
    const { x, y, height, fill } = springProps
    return (
      <g
        key={country.Country}
        onMouseOver={hoverCountry(country)}
        onMouseOut={hoverCountry(null)}
        onMouseMove={hoverCountry(country)}
        onClick={props.gotoCountryPage(country.Country)}
        onTouchEnd={hoverCountry(country)}
      >
        <animated.rect
          x={x}
          y={y}
          width={barWidth}
          transform={`translate(${-barWidth / 2} ,0)`}
          height={height}
          fill={fill}
        />
      </g>
    )
  })

  const yTicksValues = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
  const majorTickSize = 20
  const minorTickSize = majorTickSize / 2

  const selectedValueTransition = useTransition(selectedValues, null, {
    enter: selectedValues,
    update: selectedValues,
    immediate: !isInViewPort,
  })
  const yTicks = (
    <g>
      {yTicksValues.map((tick, tickIndex) => {
        const tickSize = tickIndex % 2 === 0 ? majorTickSize : minorTickSize
        const y = height - yScale(tick) + 0.5
        const label =
          tickIndex === 0 || tickIndex === yTicksValues.length - 1 ? (
            <text
              x={-tickSize - 5}
              textAnchor="end"
              y={6}
              className="graphAxisLabel"
            >
              {tick.toFixed(1)}
            </text>
          ) : null
        return (
          <g key={tick} transform={`translate(0, ${y})`}>
            <line x1={-tickSize} className="graphTick" />
            {label}
          </g>
        )
      })}
      <line className="graphTick" y2={height + 0.5} />
      <text
        className="graphAxisLabel"
        transform={`translate(-30, ${height / 2}) rotate(-90)`}
        textAnchor="middle"
      >
        SCORE
      </text>
    </g>
  )
  const selectedCountryScoreIndex =
    selectedValues !== null
      ? countriesSortedByScore.findIndex(
          (d) => d.Country === countryData.Country,
        )
      : -1
  const selectedCountryScorePercentage =
    selectedCountryScoreIndex / countriesSortedByScore.length
  const countryLabelTextAnchor =
    selectedCountryScorePercentage < 0.25
      ? "start"
      : selectedCountryScorePercentage < 0.75
      ? "middle"
      : "end"
  const countryLabelTextOffset =
    countryLabelTextAnchor === "start"
      ? -barWidth / 2
      : countryLabelTextAnchor === "end"
      ? barWidth / 2
      : 0
  const selectedValueIndicator = selectedValueTransition.map((transition) => {
    if (!transition.item || transition.state === "leave") return null

    return (
      <animated.g transform={transition.props.transform} key={transition.key}>
        <polygon points={"0,0 -5,-10 5,-10"} />
        <text
          y={-12}
          x={countryLabelTextOffset}
          textAnchor={countryLabelTextAnchor}
          filter={`url(#shadow)`}
        >
          {selectedCountry.toUpperCase()},{" "}
          {countryData[`${scoreKey}_Rank_${props.rankAttribute}`] ?? "N/A"} /{" "}
          {countryData[`Count_${props.rankAttribute}`]}
        </text>
      </animated.g>
    )
  })
  graphContents = (
    <g transform={`translate(${margins.left}, ${margins.top})`}>
      {bars}
      <text y={-12} style={{ fontStyle: "italic", fontWeight: "normal" }}>
        {title}
      </text>
      {props.axis ? (
        <g transform={`translate(${-barWidth / 2}, 0)`}>
          {yTicks}
          <line
            className="graphTick"
            x2={width + majorTickSize}
            y1={height + 0.5}
            y2={height + 0.5}
          />
          <text
            y={height + 20}
            x={xScale(0) + barWidth / 2}
            className="graphAxisLabel"
            textAnchor="middle"
          >
            {countriesSortedAlpha.length}
          </text>
          <text
            y={height + 20}
            x={xScale(countriesSortedAlpha.length - 1) + barWidth / 2}
            className="graphAxisLabel"
            textAnchor="middle"
          >
            1
          </text>
          <text
            x={width / 2}
            y={height + 20}
            className="graphAxisLabel"
            textAnchor="middle"
          >
            RANK
          </text>
        </g>
      ) : null}
      {selectedValueIndicator}
    </g>
  )

  return (
    <div ref={wrappedRef} className="countryFactorBarGraphWrapper">
      <svg width={containerWidth} height={containerHeight}>
        <defs>
          <filter id="shadow">
            <feDropShadow
              dx="1"
              dy="1"
              stdDeviation="0.5"
              floodColor="#ffffff"
            />
          </filter>
        </defs>

        {graphContents}
      </svg>
      {tooltipData ? (
        <Tooltip
          {...tooltipData}
          scoreKey={scoreKey}
          selectedYear={selectedYear}
          closeTooltip={hoverCountry(null)}
          gotoDetails={props.gotoCountryPage(tooltipData.country.Country)}
        />
      ) : null}
    </div>
  )
}

CountryFactorBarGraph.defaultProps = {
  rankAttribute: "Global",
  axis: true,
}

export default React.memo(CountryFactorBarGraph)
